import { getRouteApi, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Check, Loader2, MailCheck } from "lucide-react";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldError, fieldErrors, Notice, ProblemAlert } from "@/components/site/Feedback";
import { PhoneInput } from "@/components/site/PhoneInput";
import { Turnstile, useTurnstileState } from "@/components/site/Turnstile";
import { api } from "@/lib/api";
import { useAuth, useClientConfig } from "@/lib/hooks";
import { isMobile } from "@/lib/config";
import { DEFAULT_COUNTRY_CODE, normalizePhone, validatePhone } from "@/lib/phone";

const routeApi = getRouteApi("/signup");

const benefits = [
  "Free Blueprint book preview and download",
  "Full segmented video library with a membership",
  "AI Deal Analyzer with pay-as-you-go credits",
  "Access to scheme upgrades and strategy hours",
];

export function SignupPage() {
  usePageTitle("Join the Academy");
  const { redirect } = routeApi.useSearch();
  const { isAuthenticated } = useAuth();
  const config = useClientConfig();
  const turnstile = useTurnstileState();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", password: "", confirmPassword: "" });
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [national, setNational] = useState("");
  const [touched, setTouched] = useState(false);

  const phoneError = validatePhone(countryCode, national, true);
  const mismatch = form.confirmPassword.length > 0 && form.password !== form.confirmPassword ? "The passwords do not match." : null;

  const register = useMutation({
    mutationFn: () =>
      api.auth.register(
        {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phoneNumber: normalizePhone(countryCode, national),
          password: form.password,
          confirmPassword: form.confirmPassword,
        },
        turnstile.token,
      ),
    onError: () => turnstile.reset(),
  });

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const closed = config.data && !config.data.allowUserRegistration;

  return (
    <div className="mx-auto grid max-w-5xl gap-12 px-5 py-24 lg:grid-cols-2">
      <div>
        <p className="eyebrow text-gold">{isMobile ? "Academy Portal" : "Academy Portal · £10/mo"}</p>
        <h1 className="mt-3 text-4xl">Create your account</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {isMobile
            ? "Your account works on the website and in the app."
            : "Create a free account, then add the membership (£10/mo incl. UK VAT) whenever you are ready. Cancel any time from your billing page."}
        </p>
        <div className="mt-8 space-y-3">
          {benefits.map((f) => (
            <div key={f} className="flex items-start gap-3 text-sm">
              <Check className="mt-0.5 size-4 shrink-0 text-gold" />
              {f}
            </div>
          ))}
        </div>
      </div>

      <div className="h-fit rounded-lg border border-border bg-card p-8 shadow-elevated">
        {register.isSuccess ? (
          <div className="space-y-4">
            <MailCheck className="size-8 text-gold" />
            <h2 className="text-3xl">Check your inbox</h2>
            <p className="text-sm text-muted-foreground">
              We sent a confirmation link to <span className="font-medium text-foreground">{form.email}</span>.
              Confirm your email address, then sign in.
            </p>
            <Button asChild variant="gold" size="lg" className="w-full">
              <Link to="/login" search={{ redirect }}>
                Go to sign in
              </Link>
            </Button>
            <p className="text-xs text-muted-foreground">
              Nothing arrived?{" "}
              <Link to="/resend-confirmation" search={{ email: form.email }} className="underline underline-offset-4">
                Send it again
              </Link>
            </p>
          </div>
        ) : closed ? (
          <Notice title="Registration is closed">New accounts can't be created right now. Please contact us.</Notice>
        ) : (
          <form
            className="space-y-5"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              setTouched(true);
              if (phoneError || mismatch || turnstile.missing) return;
              register.mutate();
            }}
          >
            {isAuthenticated && <Notice>You are already signed in.</Notice>}
            <ProblemAlert error={register.error} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">First name</Label>
                <Input id="firstName" autoComplete="given-name" required maxLength={100} value={form.firstName} onChange={set("firstName")} />
                <FieldError messages={fieldErrors(register.error, "FirstName")} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last name</Label>
                <Input id="lastName" autoComplete="family-name" required maxLength={100} value={form.lastName} onChange={set("lastName")} />
                <FieldError messages={fieldErrors(register.error, "LastName")} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-email">Email</Label>
              <Input id="signup-email" type="email" autoComplete="email" required placeholder="you@example.com" value={form.email} onChange={set("email")} />
              <FieldError messages={fieldErrors(register.error, "Email")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-phone">Phone number</Label>
              <PhoneInput
                id="signup-phone"
                countryCode={countryCode}
                national={national}
                invalid={touched && !!phoneError}
                onChange={(c, n) => {
                  setCountryCode(c);
                  setNational(n);
                }}
              />
              <FieldError messages={[touched ? phoneError : null, ...(fieldErrors(register.error, "PhoneNumber") ?? [])]} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-password">Password</Label>
              <Input id="signup-password" type="password" autoComplete="new-password" required placeholder="••••••••" value={form.password} onChange={set("password")} />
              <FieldError messages={fieldErrors(register.error, "Password")} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-confirm">Confirm password</Label>
              <Input id="signup-confirm" type="password" autoComplete="new-password" required placeholder="••••••••" value={form.confirmPassword} onChange={set("confirmPassword")} />
              <FieldError messages={[mismatch, ...(fieldErrors(register.error, "ConfirmPassword") ?? [])]} />
            </div>
            <Turnstile ref={turnstile.ref} onToken={turnstile.setToken} onEnabled={turnstile.setEnabled} />
            <Button
              type="submit"
              variant="gold"
              size="lg"
              className="w-full"
              disabled={register.isPending || turnstile.missing || !form.firstName || !form.lastName || !form.email || !form.password}
            >
              {register.isPending && <Loader2 className="animate-spin" />}
              Create my account
            </Button>
            <p className="text-xs text-muted-foreground">
              By creating an account you agree to our{" "}
              <Link to="/legal/terms" className="underline underline-offset-4">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/legal/privacy" className="underline underline-offset-4">
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        )}
        {!register.isSuccess && (
          <p className="mt-6 text-sm text-muted-foreground">
            Already a member?{" "}
            <Link to="/login" search={{ redirect }} className="font-medium text-foreground underline underline-offset-4">
              Sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
